import { Form, Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useCampaignLoadQuery, useCampaignUpdateMutation } from "../../services/server/CampaignApi";
import type { CampaignEntity } from "../../types/CampaignEntity";
import campaignEditJson from "./campaign-terminals-edit.json";

export const CampaignTerminalsEditForm: FC<FormProps> = ({ runSubmit }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [form] = Form.useForm();

    const formDefinition = campaignEditJson as FormJson;

    const [formData, setFormData] = useState<CampaignEntity>({} as CampaignEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useCampaignLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCampaignUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            // Get fresh values from form instance to capture CSV imports and other changes
            // that may not have propagated to formData due to React batching
            const freshValues = form.getFieldsValue();
            const dataToSave = { ...formData, ...freshValues };

            const result = await saveFormData({ data: dataToSave });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "Campaign",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.CAMPAIGNS}`);
            }
        },
        [saveFormData, formData, form, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CampaignEntity>
                    name="CampaignTerminalsEditForm"
                    customFormInstance={form}
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
